import { GridRenderCellParams } from '@mui/x-data-grid';
import { DataTableColumn } from 'components/data/DataTable/DataTable';
import { SupplierUtilCell } from 'components/DataGridCells/SupplierUtilCell/SupplierUtilCell';
import { format } from 'date-fns';
import {
    formatDecimalToFixedNumber,
    formatDecimalToFixedPercent,
} from 'helpers';
import { getDateWithTime, getMonthDayYear } from 'helpers/dates';
import { Supplier } from 'types';

export const supplierColumns: DataTableColumn[] = [
    {
        headerName: 'Name',
        field: 'name',
        valueGetter: (params) => {
            return params.row?.info?.name;
        },
        minWidth: 250,
    },
    {
        headerName: 'Supplier Id',
        field: 'supplierId',
        minWidth: 200,
    },
    {
        headerName: 'Device Count',
        field: 'deviceCount',
        valueGetter: (params) => params.row?.info?.stat?.deviceCount,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
    },
    {
        headerName: 'Last Deploy Date',
        field: 'lastDeployedTime',
        valueGetter: (params) =>
            params.row?.info?.stat?.lastDeployedTime
                ? format(
                      params.row?.info?.stat?.lastDeployedTime,
                      'MM/dd/yy hh:mm a'
                  )
                : '',
        minWidth: 200,
    },
    {
        headerName: 'Active',
        field: 'active',
        hideByDefault: true,
        valueGetter: (params) => {
            return params.row?.info?.active;
        },
    },
    {
        headerName: 'Shipped Count',
        field: 'shippedCount',
        hideByDefault: true,
        valueGetter: (params) => {
            const shippedCount = params.row?.info?.stat?.status?.shipped;

            return shippedCount;
        },
    },
    {
        headerName: 'Deploy Count',
        field: 'deployCount',
        hideByDefault: true,
        valueGetter: (params) => {
            const deployedCount = params.row?.info?.stat?.status?.deployed;

            return deployedCount;
        },
    },
    {
        headerName: 'Deploy Pct',
        field: 'deployPct',
        hideByDefault: true,
        valueGetter: (params) => {
            const deployedPct =
                (params.row?.info?.stat?.status?.deployed /
                    (params.row?.info?.stat?.status?.shipped +
                        params.row?.info?.stat?.status?.deployed +
                        params.row?.info?.stat?.status?.sleep)) *
                100;

            return formatDecimalToFixedPercent(deployedPct, 0);
        },
    },
    {
        headerName: 'Connect Count',
        field: 'connectCount',
        hideByDefault: true,
        valueGetter: (params) => {
            return params.row?.info?.stat?.connectCount;
        },
    },
    {
        headerName: 'Connect Pct',
        field: 'connectPct',
        hideByDefault: true,
        valueGetter: (params) => {
            return formatDecimalToFixedPercent(
                params.row?.info?.stat?.connectPct * 100,
                0
            );
        },
    },
    {
        headerName: 'Total Customers',
        field: 'total_customers',
        hideByDefault: true,
        valueGetter: (params) => {
            return params.row.info?.total_customers;
        },
    },
    {
        headerName: 'Monitored Pct',
        field: 'monitoredPct',
        hideByDefault: true,
        valueGetter: (params) => {
            const monitoredPct =
                ((params.row?.info?.stat?.status?.deployed +
                    params.row?.info?.stat?.status?.shipped +
                    params.row?.info?.stat?.status?.sleep) /
                    params?.row?.info?.total_customers) *
                100;

            return formatDecimalToFixedPercent(monitoredPct, 0);
        },
    },
    {
        headerName: 'First Deployed Time',
        field: 'firstDeployedTime',
        hideByDefault: true,
        valueGetter: (params) => {
            const time = params.row?.info?.stat?.firstDeployedTime;
            return time ? getDateWithTime(time) : 'N/A';
        },
        minWidth: 200,
    },
    {
        headerName: 'Last Login Portal',
        field: 'lastLoginPortal',
        hideByDefault: true,
        valueGetter: (params) =>
            params.row?.info?.lastLoginPortal
                ? getMonthDayYear(params.row?.info?.lastLoginPortal)
                : 'N/A',
        minWidth: 200,
    },
    {
        headerName: 'Benchmark',
        field: 'benchmark',
        hideByDefault: true,
        valueGetter: (params) => {
            return params.row.info?.rank;
        },
    },
    {
        headerName: 'Benchmark Avg',
        field: 'benchmark_avg',
        hideByDefault: true,
        valueGetter: (params) => {
            return formatDecimalToFixedNumber(params.row.info?.rank_avg, 0);
        },
    },
    {
        headerName: 'Total Capacity',
        field: 'total_capacity',
        hideByDefault: true,
        valueGetter: (params) => {
            const totalCapacity =
                params.row.info?.stat?.inventory?.all?.total_capacity;
            return totalCapacity ? Math.round(parseInt(totalCapacity)) : null;
        },
    },
    {
        headerName: 'Total Fill',
        field: 'total_fill',
        hideByDefault: true,
        valueGetter: (params) => {
            const totalFill = params.row.info?.stat?.inventory?.all?.total_fill;

            return totalFill ? Math.round(parseInt(totalFill)) : null;
        },
    },
    {
        headerName: 'Back Office',
        field: 'backoffice',
        hideByDefault: true,
        valueGetter: (params) => {
            return params.row.info?.backoffice;
        },
    },
    {
        headerName: 'Salesforce Id',
        field: 'salesforce_id',
        hideByDefault: true,
        valueGetter: (params) => {
            return params.row.info?.salesforce_id;
        },
        minWidth: 200,
    },
    {
        headerName: 'Csm Name',
        field: 'csm_name',
        hideByDefault: true,
        valueGetter: (params) => {
            return params.row.info?.csm_name;
        },
        minWidth: 200,
    },
    {
        headerName: 'Util',
        field: 'util',
        hideByDefault: true,
        sortable: false,
        renderCell: (params: GridRenderCellParams<any, Supplier, any>) => {
            const supplier = params.row;

            return <SupplierUtilCell supplier={supplier} />;
        },
    },
];
